const SAME_ORIGIN = new Set(["", "same-origin", "same_origin", "/"]);

/** True when API calls should use the page origin (nginx/dev proxy handles upstream). */
export function isSameOriginGatewayMode(raw: string): boolean {
  return SAME_ORIGIN.has(raw.trim().toLowerCase());
}

/**
 * API gateway base URL from Create React App env — single place, no hardcoded hosts.
 * Accepts the same variables commonly used across this repo.
 * Use `same-origin` (or leave empty) so nginx/dev proxy forwards to the real gateway.
 */
export function gatewayBaseUrlFromEnv(): string {
  const raw = (
    process.env.REACT_APP_API_GATEWAY_URL ||
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    ""
  ).trim();
  if (isSameOriginGatewayMode(raw)) {
    return "";
  }
  if (!raw) {
    throw new Error(
      "Set one of REACT_APP_API_GATEWAY_URL, REACT_APP_API_URL, or REACT_APP_API_BASE_URL in your environment (e.g. `.env`).",
    );
  }
  return raw.replace(/\/$/, "");
}

/** Browser origin when env is `same-origin` (nginx/dev proxy forwards to the real gateway). */
export function resolveGatewayBaseUrl(): string {
  const fromEnv = gatewayBaseUrlFromEnv();
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "";
}

/**
 * Absolute gateway HTTP origin for shared-dashboards (Video Agent, GPU WS, etc.).
 * When HTTP is same-origin, use REACT_APP_GATEWAY_UPSTREAM so agent APIs hit
 * api.edge.variphi.com — not the SPA host (niadit.com/vgi-agent/…).
 */
export function resolveGatewayUpstreamHttpUrl(): string {
  const upstream = (process.env.REACT_APP_GATEWAY_UPSTREAM ?? "").trim();
  if (upstream && !isSameOriginGatewayMode(upstream)) {
    const withScheme =
      upstream.startsWith("http://") || upstream.startsWith("https://")
        ? upstream
        : `https://${upstream}`;
    return withScheme.replace(/\/$/, "");
  }
  return resolveGatewayBaseUrl();
}

/** WebSocket gateway origin — same-origin apps use the SPA host (nginx proxies /analytic, /ws). */
export function resolveWebSocketGatewayOrigin(): string {
  const wsEnv = (process.env.REACT_APP_WS_GATEWAY_URL ?? "").trim();
  if (wsEnv && !isSameOriginGatewayMode(wsEnv)) {
    return wsEnv
      .replace(/^https/i, "wss")
      .replace(/^http/i, "ws")
      .replace(/\/$/, "");
  }

  const apiEnv = (
    process.env.REACT_APP_API_GATEWAY_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).trim();
  if (isSameOriginGatewayMode(wsEnv) || isSameOriginGatewayMode(apiEnv)) {
    if (typeof window !== "undefined") {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${window.location.host}`;
    }
    return "ws://localhost:4702";
  }

  const http = resolveGatewayUpstreamHttpUrl();
  if (!http) {
    if (typeof window !== "undefined") {
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${window.location.host}`;
    }
    return "ws://localhost:4702";
  }
  return http.replace(/^https/i, "wss").replace(/^http/i, "ws").replace(/\/$/, "");
}

/** Snapshot capture host for CMM Zone "Take Snapshot" (proxied via /cmm/{host}/…). */
export function snapshotApiUrlFromEnv(): string {
  return (
    process.env.REACT_APP_SNAPSHOT_API_URL ||
    process.env.REACT_APP_SNAPSHOT_DOMAIN ||
    ""
  )
    .trim()
    .replace(/\/$/, "");
}

/**
 * Values passed into `@variphigen/shared-dashboards` — read from env only at the app shell.
 * Uses the real gateway upstream so Video Agent hits api.edge.variphi.com/vgi-agent/…
 * (not the SPA origin when REACT_APP_API_GATEWAY_URL is same-origin).
 */
export function loadSharedDashboardConfig(): {
  apiGatewayUrl: string;
  applicationId: string;
  snapshotApiUrl?: string;
} {
  const snapshotApiUrl = snapshotApiUrlFromEnv();
  return {
    apiGatewayUrl: resolveGatewayUpstreamHttpUrl(),
    applicationId: (process.env.REACT_APP_APPLICATION_ID ?? "").trim(),
    ...(snapshotApiUrl ? { snapshotApiUrl } : {}),
  };
}

/** Cleaning metrics API — dedicated URL if set, otherwise the main gateway. */
export function cleaningApiBaseUrlFromEnv(): string {
  const dedicated = (process.env.REACT_APP_CLEANING_API_URL ?? "").trim();
  if (dedicated) return dedicated.replace(/\/$/, "");
  return gatewayBaseUrlFromEnv();
}
